// src/API/rest.taskStatus.ts

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRestAPI } from './axiosAPI';

// === TYPES ===
export interface ForecastCalenderLine {
  FT07_Date: string;
  FT07_TargetDate: string;
}

export interface Notification {
  FT08_ForecastCalenderResponsibilityID: number;
  FT08_M03_EmployeeID: number;
  FT08_is_completed: boolean;
  FT08_completed_date: string | null;
  FT08_Status: number;
  FT06_action: string;
  FT08_T07_ForecastCalenderline: ForecastCalenderLine;
}

interface NotificationResponse {
  status: string;
  data: Notification[];
  message?: string;
}

interface LatestDateResponse {
  success: boolean;
  message: string;
  data: { date: string };
}

// === QUERY KEYS ===
const TASK_STATUS_QUERY_KEY = 'taskStatus';
const LATEST_DATE_QUERY_KEY = 'latestCalendarDate';
const CALENDAR_QUERY_KEY = 'forcastCalendersList';

// === HELPER ===
const isValidDateString = (str: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(str);

// === Fetch latest date ===
const fetchLatestDate = async (api: ReturnType<typeof useRestAPI>): Promise<string> => {
  const response = await api.get<LatestDateResponse>({
    url: `${process.env.REACT_APP_BACKEND_URL}/transactions/calendar/get-latest-date/`,
    mode: 'protected',
  });

  // api.get already returns the data, not axios response
  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch latest date');
  }
  return response.data.date;
};

// === useTaskStatus Hook ===
export const useTaskStatus = () => {
  const api = useRestAPI();
  const queryClient = useQueryClient();

  const getCachedActiveDate = (): string | null => {
    const queries = queryClient.getQueriesData({ queryKey: [CALENDAR_QUERY_KEY] });
    for (const [key] of queries) {
      if (Array.isArray(key) && typeof key[1] === 'string' && isValidDateString(key[1])) {
        return key[1];
      }
    }
    return null;
  };

  const cachedDate = getCachedActiveDate();

  const { data: latestDate } = useQuery({
    queryKey: [LATEST_DATE_QUERY_KEY],
    queryFn: () => fetchLatestDate(api),
    enabled: !cachedDate,
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  const activeDate = cachedDate || latestDate;

  return useQuery<NotificationResponse, Error>({
    queryKey: [TASK_STATUS_QUERY_KEY, activeDate],
    queryFn: async () => {
      if (!activeDate) throw new Error('No active date available');

      const response = await api.get<NotificationResponse>({
        url: `${process.env.REACT_APP_BACKEND_URL}/transactions/check-responsibility-completed/${activeDate}/`,
        mode: 'protected',
      });

      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to fetch notifications');
      }
      return response;
    },
    enabled: !!activeDate,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

// === useMarkTaskCompleted Hook ===
export const useMarkTaskCompleted = () => {
  const api = useRestAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, employee_id }: { id: number; employee_id: number }) => {
      const response = await api.patch<NotificationResponse, { employee_id: number; FT08_is_completed: boolean }>({
        url: `${process.env.REACT_APP_BACKEND_URL}/transactions/update-responsibility/${id}/`,
        mode: 'protected',
        data: {
          employee_id,
          FT08_is_completed: true,
        },
      });

      // Your axiosAPI wrapper returns the data directly
      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to mark task as completed');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_STATUS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CALENDAR_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LATEST_DATE_QUERY_KEY] });
    },
  });
};